// src/pages/TripDetailPage.tsx
import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { generateClient } from 'aws-amplify/api';
import { Helmet } from 'react-helmet-async';

import {
  View,
  Heading,
  Text,
  Flex,
  Button,
  Loader,
  Divider,
  Alert
} from '@aws-amplify/ui-react';
import { ChevronLeft, Users } from 'lucide-react';
import { useAuth } from '../AuthContext';
import './TripDetailPage.css';
import '../components/trip/MemberSelection.css';
import { getTripDetails, processInvitationCustom } from '../graphql/customQueries';
import { TabName } from '../components/trip/TabNavigation';
import {
  loadPhotoThumbnailUrl,
  loadPhotoUrl
} from '../services/enhancedPhotoUrlService';

// Import components
import TabNavigation from '../components/trip/TabNavigation';
import PhotosTab from '../components/trip/PhotosTab';
import MembersTab from '../components/trip/MembersTab';
import StorageTab from '../components/trip/StorageTab';
import StorageUpgradeModal from '../components/StorageUpgradeModal'

// Create a client for API calls
const client = generateClient();

interface TripDetailParams {
  tripId: string;
}

const TripDetailPage: React.FC = () => {
  const { tripId } = useParams<keyof TripDetailParams>() as TripDetailParams;
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const processingRef = useRef(false);

  // State for trip data
  const [trip, setTrip] = useState<any>(null);
  const [photos, setPhotos] = useState<any[]>([]);
  const [photoUrls, setPhotoUrls] = useState<{ [key: string]: string }>({});
  const [thumbnailUrls, setThumbnailUrls] = useState<{ [key: string]: string }>({});
  const [members, setMembers] = useState<any[]>([]);

  // State for UI
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<TabName>("photos");

  // State for invitation processing
  const [processingInvite, setProcessingInvite] = useState<boolean>(false);
  const [inviteSuccess, setInviteSuccess] = useState<boolean>(false);
  const [inviteMessage, setInviteMessage] = useState<string | null>(null);

  //states for storage upgrades
  const [showStorageUpgradeModal, setShowStorageUpgradeModal] = useState<boolean>(false);
  const [upgradeContext, setUpgradeContext] = useState<string | null>(null);

  // Extract invitation token from URL if present
  const urlParams = new URLSearchParams(location.search);
  const inviteToken = urlParams.get('invite');

  const needsInviteProcessing = inviteToken && !inviteSuccess && isAuthenticated;
  // Effect to handle invitation token in URL
  useEffect(() => {
    if (needsInviteProcessing && tripId && !processingRef.current) {

      processingRef.current = true; // Mark as processing immediately
      processInviteToken();
    }
  }, [needsInviteProcessing, tripId, isAuthenticated]);

  // Fetch trip details on mount or when auth state changes
  useEffect(() => {
    if (!tripId || authLoading) return;
    if (processingRef.current) return;
    if (trip?.id === tripId) return;
    fetchTripDetails();
  }, [tripId, isAuthenticated, authLoading, needsInviteProcessing]);

  // Add a function to handle storage limit errors
  const handleStorageLimitExceeded = () => {
    setShowStorageUpgradeModal(true);
  };

  // Function to process invitation token
  const processInviteToken = async () => {
    try {
      setProcessingInvite(true);
      setLoading(true);
      setError(null);


      const result: any = await client.graphql({
        query: processInvitationCustom,
        variables: {
          tripId,
          token: inviteToken
        },
        authMode: "userPool"
      });

      const response = result.data.processInvitation;
      // Update trip data from response
      setTrip(response.trip);
      setMembers(response.trip.members || []);
      setPhotos(response.trip.photos || []);
      setUserRole(response.trip.userRole);

      // Set invitation success message
      if (response.invitationProcessed) {
        setInviteSuccess(true);
        setInviteMessage(response.message || 'You have been added to this album!');

        // Remove the token from URL without page refresh
        const newUrl = window.location.pathname;
        window.history.replaceState({}, '', newUrl);
      }

    } catch (error: any) {
      console.error('Error processing invitation:', error);
      if (error.errors[0].message?.includes('Invitation has expired')) {
        setError('This invitation link has expired. Ask the album member to send it again.');
      } else {
        setError(error.message || 'Failed to process invitation. Please try again.');
      }

      // If access denied, redirect to home
      if (error.message?.includes('do not have access')) {
        setTimeout(() => navigate('/trips'), 3000);
      }
    } finally {
      setProcessingInvite(false);
      setLoading(false);
      processingRef.current = false; // Reset the processing ref
    }
  };

  // Request to load a photo URL
  const handleLoadPhotoUrl = (photoId: string, url: string, isThumbnail: boolean = false) => {
    if (isThumbnail) {
      setThumbnailUrls(prev => ({
        ...prev,
        [photoId]: url
      }));
    } else {
      setPhotoUrls(prev => ({
        ...prev,
        [photoId]: url
      }));
    }
  };

  // Fetch trip details
  const fetchTripDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      if (trip && trip.id === tripId) {
        setLoading(false);
        return;
      }
      const tripData: any = await client.graphql({
        query: getTripDetails,
        variables: { tripId },
        authMode: "userPool"
      });

      const tripDetails = tripData.data.getTripDetails;

      if (!tripDetails) {
        setError('Trip not found');
        setLoading(false);
        return;
      }

      setTrip(tripDetails);

      // Members are already formatted properly
      setMembers(tripDetails.members || []);

      // Set user's role from the response
      setUserRole(tripDetails.userRole);

      // Photos are already formatted with uploader names
      if (tripDetails.photos) {
        setPhotos(tripDetails.photos);
      }

    } catch (err: any) {
      console.error('Error fetching trip details:', err);
      setError(err.message || 'Failed to load trip details');

      // If access denied, redirect to home
      if (err.message?.includes('do not have access')) {
        setTimeout(() => navigate('/trips'), 3000);
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle photo upload
  const handlePhotoUploaded = (newPhoto: any) => {
    // Add the new photo to the beginning of the list
    setPhotos(prev => [newPhoto, ...prev]);
  };

  // Handle photo deletion
  const handlePhotoDeleted = (photoIds: string[]) => {
    // Remove deleted photos from the list
    setPhotos(prev => prev.filter(photo => !photoIds.includes(photo.id)));
  };

  // Handle member removal with optional photo deletion
  const handleMemberRemoved = (memberId: string, deletedPhotoIds?: string[]) => {
    // Filter out the removed member from the list
    setMembers(prev => prev.filter(member => member.id !== memberId));

    // If photos were deleted, update the photos list too
    if (deletedPhotoIds && deletedPhotoIds.length > 0) {
      setPhotos(prev => prev.filter(photo => !deletedPhotoIds.includes(photo.id)));
    }
  };

  // Format date helper
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString();
  };

  if (authLoading) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
        <Text marginTop="1rem">Verifying access...</Text>
      </View>
    );
  }

  // Show authentication required message if not authenticated
  if (!isAuthenticated) {
    return (
      <View padding="2rem" textAlign="center">
        <View className="w-16 h-16 bg-blue-900 rounded-full flex items-center justify-center mx-auto mb-4">
          <Users size={32} className="text-blue-400" />
        </View>
        <Heading level={3} marginBottom="1rem">Sign in to view this album</Heading>
        <Text marginBottom="2rem">You need to sign in or create an account to view this album.</Text>
        <Button
          onClick={() => {
            if (inviteToken) {
              const inviteInfo = {
                tripId,
                token: inviteToken
              };
              localStorage.setItem('pendingInviteInfo', JSON.stringify(inviteInfo));
            }
            navigate('/login');
          }}
          variation="primary"
          size="large"
        >
          Sign in to View Album
        </Button>
      </View>
    );
  }

  if (loading || processingRef.current) {
    return (
      <View padding="2rem" textAlign="center">
        <Loader size="large" />
        <Text marginTop="1rem">
          {processingInvite ? 'Adding you to the album...' : 'Loading album...'}
        </Text>
      </View>
    );
  }

  if (error) {
    return (
      <View padding="2rem" textAlign="center">
        <Text>{error}</Text>
        <Button onClick={() => navigate('/trips')} marginTop="1rem">
          Back to Albums
        </Button>
      </View>
    );
  }

  if (!trip) {
    return (
      <View padding="2rem" textAlign="center">
        <Text>Trip not found</Text>
        <Button onClick={() => navigate('/trips')} marginTop="1rem">
          Back to Albums
        </Button>
      </View>
    );
  }

  return (
    <View padding="2rem">
      <Helmet>
        <title>{trip ? `${trip.tripName} - Banjara App` : 'Trip Details - Banjara App'}</title>
      </Helmet>
      <StorageUpgradeModal
        isOpen={showStorageUpgradeModal}
        onClose={() => {
          setShowStorageUpgradeModal(false);
          setUpgradeContext(null);
          
        }}
        currentTier={user.banjaraUser.storageTier}
        fromUploadError={upgradeContext === 'upload'}
        onUpgradeComplete={() => {
          // After successful upgrade, you might want to:
          setShowStorageUpgradeModal(false);
          // Refresh user data
          fetchTripDetails();
        }}
      />
      {/* Invitation Success Message */}
      {inviteSuccess && inviteMessage && (
        <Alert
          variation="success"
          isDismissible={true}
          onDismiss={() => setInviteSuccess(false)}
          marginBottom="1rem"
        >
          {inviteMessage}
        </Alert>
      )}

      <Flex direction="column" gap="1rem">
        {/* Trip Header */}
        <Flex direction="row" justifyContent="space-between" alignItems="center" marginBottom="1rem">
          <div>
            <Button onClick={() => navigate('/trips')} marginBottom="0.5rem">
              <ChevronLeft size={16} />
              Back to Albums
            </Button>
            <Heading level={1}>{trip.tripName}</Heading>
            {trip.description && (
              <Text marginTop="0.5rem">{trip.description}</Text>
            )}
            <Text fontSize="0.9rem" marginTop="0.5rem">
              {trip.startDate && (
                <>
                  {formatDate(trip.startDate)} -
                  {trip.endDate ? formatDate(trip.endDate) : 'Ongoing'}
                </>
              )}
            </Text>
          </div>
        </Flex>

        <Divider />

        {/* Tab Navigation */}
        <TabNavigation
          activeTab={activeTab}
          photoCount={photos.length}
          memberCount={members.length}
          storageUsed={trip.storageUsed || 0}
          onTabChange={(tabName) => {
            // If user is not the creator and tries to access storage tab, don't change
            if (tabName === "storage" && userRole !== "CREATOR") {
              return;
            }
            setActiveTab(tabName);
          }}
          userRole={userRole} // Pass userRole to TabNavigation
        />

        {/* Tab Content */}
        <div className="tab-content">
          {activeTab === "photos" ? (
            <PhotosTab
              photos={photos}
              tripId={tripId}
              userRole={userRole}
              photoUrls={photoUrls}
              thumbnailUrls={thumbnailUrls}
              onPhotoUploaded={handlePhotoUploaded}
              onPhotoDeleted={handlePhotoDeleted}
              onLoadPhotoUrl={handleLoadPhotoUrl}
              onStorageLimitExceeded={handleStorageLimitExceeded} 
            />
          ) : activeTab === "members" ? (
            <MembersTab
              members={members}
              tripId={tripId}
              tripName={trip.tripName}
              userRole={userRole}
              userId={user?.sub}
              onMemberRemoved={handleMemberRemoved}
            />
          ) : userRole === "CREATOR" ? (
            <StorageTab
              photos={photos}
              photoUrls={thumbnailUrls} // Use thumbnails for storage tab
              storageUsed={trip.storageUsed || 0}
              userRole={userRole}
              onPhotoDeleted={handlePhotoDeleted}
            />
          ) : (
            // Fallback if somehow a non-creator lands on storage tab
            <PhotosTab
              photos={photos}
              tripId={tripId}
              userRole={userRole}
              photoUrls={photoUrls}
              thumbnailUrls={thumbnailUrls}
              onPhotoUploaded={handlePhotoUploaded}
              onPhotoDeleted={handlePhotoDeleted}
              onLoadPhotoUrl={handleLoadPhotoUrl}
              onStorageLimitExceeded={handleStorageLimitExceeded} 
            />
          )}
        </div>
      </Flex>
    </View>
  );
};

export default TripDetailPage;